/**
 * Network interception module for capturing fetch/XHR errors
 */

import type { NetworkErrorDetails, RRWebEvent } from './types';
import { sessionState } from './state';
import { recordActivity } from './activity';
import { MAX_NETWORK_ENDPOINTS } from './constants';
import { isIgnoredNetworkError } from './noise-filter';
import { redactUrl } from './redact';

/**
 * Normalize URL for consistent endpoint grouping.
 * Strips query string and hash; keeps protocol + host + pathname.
 */
function normalizeUrl(url: string): string {
  try {
    const u = new URL(url, window.location.href);
    return `${u.protocol}//${u.host}${u.pathname}`;
  } catch {
    return url;
  }
}

/**
 * Update per-endpoint aggregated stats in memory.
 * Capped at MAX_NETWORK_ENDPOINTS to prevent unbounded growth.
 */
function updateEndpointStats(
  pattern: string,
  method: string,
  duration: number,
  isError: boolean
): void {
  const key = `${method.toUpperCase()} ${pattern}`;
  let stats = sessionState.networkEndpointStats[key];
  if (!stats) {
    const keys = Object.keys(sessionState.networkEndpointStats);
    if (keys.length >= MAX_NETWORK_ENDPOINTS) {
      // Drop new endpoints beyond cap to limit memory
      return;
    }
    stats = {
      pattern,
      method: method.toUpperCase(),
      requestCount: 0,
      errorCount: 0,
      totalResponseTime: 0,
    };
    sessionState.networkEndpointStats[key] = stats;
  }
  stats.requestCount++;
  stats.totalResponseTime += duration;
  if (isError) {
    stats.errorCount++;
  }
}

/**
 * Record a network error in the same format as console errors
 * so the backend processes them identically
 */
export function recordNetworkError(details: NetworkErrorDetails): void {
  // Failures of analytics/ads domains are ad-blocker artifacts, not product
  // bugs — don't turn them into session errors.
  if (isIgnoredNetworkError(details.url)) {
    return;
  }

  sessionState.networkErrorCount++;
  recordActivity();

  // Request URLs can carry tokens/API keys in their query string, and the URL
  // is interpolated straight into the stored error message. Redact here so no
  // caller can leak one by forgetting to.
  const safeUrl = redactUrl(details.url);

  // Build error message in a readable format
  let errorMessage: string;
  if (details.status) {
    errorMessage = `Network Error: ${details.method} ${safeUrl} failed with status ${details.status} ${details.statusText || ""}`.trim();
  } else {
    errorMessage = `Network Error: ${details.method} ${safeUrl} - ${details.errorMessage || "Request failed"}`;
  }

  // Push event in the same format as rrweb console plugin errors
  // Type 6 is Plugin event in rrweb
  const networkErrorEvent: RRWebEvent = {
    type: 6,
    data: {
      plugin: "rrweb/console@1",
      payload: {
        level: "error",
        payload: [errorMessage]
      }
    },
    timestamp: Date.now()
  };

  sessionState.events.push(networkErrorEvent);
}

/**
 * Intercept fetch to capture network errors
 */
export function interceptFetch(): void {
  const originalFetch = window.fetch;

  window.fetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const method = init?.method || "GET";
    const startTime = performance.now();

    // Don't intercept our own event sending requests
    if (url === sessionState.backendUrl) {
      return originalFetch.apply(window, [input, init]);
    }

    return originalFetch.apply(window, [input, init])
      .then(function (response: Response) {
        const duration = Math.round(performance.now() - startTime);
        const isError = !response.ok;
        updateEndpointStats(normalizeUrl(url), method, duration, isError);

        // Record failed responses (4xx and 5xx status codes)
        if (isError) {
          recordNetworkError({
            url: url,
            method: method.toUpperCase(),
            status: response.status,
            statusText: response.statusText,
            type: "fetch"
          });
        }
        return response;
      })
      .catch(function (error: Error) {
        const duration = Math.round(performance.now() - startTime);
        updateEndpointStats(normalizeUrl(url), method, duration, true);
        // Record network errors (connection failures, CORS, etc.)
        recordNetworkError({
          url: url,
          method: method.toUpperCase(),
          errorMessage: error.message,
          type: "fetch"
        });
        throw error; // Re-throw to not break the application
      });
  };
}

/**
 * Intercept XMLHttpRequest to capture network errors
 */
export function interceptXHR(): void {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null
  ): void {
    (this as any)._trackingUrl = typeof url === "string" ? url : url.href;
    (this as any)._trackingMethod = method;
    (this as any)._trackingStartTime = performance.now();
    return originalOpen.apply(this, [method, url, async !== false, username, password]);
  };

  XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null): void {
    const xhr = this;
    const url = (xhr as any)._trackingUrl;
    const method = (xhr as any)._trackingMethod;

    // Don't intercept our own event sending requests
    if (url === sessionState.backendUrl) {
      return originalSend.apply(xhr, [body]);
    }

    xhr.addEventListener("load", function () {
      const duration = Math.round(performance.now() - ((xhr as any)._trackingStartTime || 0));
      const isError = xhr.status >= 400;
      updateEndpointStats(normalizeUrl(url), method, duration, isError);

      // Record failed responses (4xx and 5xx status codes)
      if (isError) {
        recordNetworkError({
          url: url,
          method: method.toUpperCase(),
          status: xhr.status,
          statusText: xhr.statusText,
          type: "xhr"
        });
      }
    });

    xhr.addEventListener("error", function () {
      const duration = Math.round(performance.now() - ((xhr as any)._trackingStartTime || 0));
      updateEndpointStats(normalizeUrl(url), method, duration, true);
      // Record network errors
      recordNetworkError({
        url: url,
        method: method.toUpperCase(),
        errorMessage: "Network request failed",
        type: "xhr"
      });
    });

    return originalSend.apply(xhr, [body]);
  };
}
